import React, { useCallback, useMemo } from "react";
import { ApolloError } from "@apollo/client";
import { useI18n } from "../../hooks/i18n-hooks";
import Loading from "../../components/Loading";
import { EarningType, RepetitionType, SummaryRow } from "../../__generated__/graphql";
import VStack from "../../ui_atoms/VStack";
import { TitleMediumText } from "../../components/FormattedText";
import Text from "../../components/Text";
import LabelValueText from "../../components/LabelValueText";
import { formatCurrency } from "../../utils";
import Callout from "../../ui_atoms/Callout";
import ScreenCardHeadband from "../ScreenCardHeadband";

type SummaryRowWithSubValues = SummaryRow & {
    subValues?: SummaryRowWithSubValues[];
};

type PayrollBreakdownSummaryProps = {
    rows?: SummaryRow[];
    needCompute?: boolean;
    loading: boolean;
    error?: ApolloError;
    contractEarningType?: EarningType | null;
};

const PayrollBreakdownSummary = ({
    rows = [],
    needCompute = false,
    loading,
    error,
    contractEarningType,
}: PayrollBreakdownSummaryProps) => {
    const i18n = useI18n();

    const summaryRows = useMemo(
        () =>
            rows.map((v) =>
                v.repetition === RepetitionType.Count ? { ...v, category: "variable_fee" } : v,
            ),
        [rows],
    );

    const getSubLabelValue = useCallback(
        (item: SummaryRow) => {
            const { category, productKey, quantity, description, unitPrice } = item;

            const title = i18n.t(`screens.payroll_breakdown_editor.product_keys.${productKey}`, {
                quantity,
                unitPrice: formatCurrency({
                    amountInCents: item.unitPrice,
                }),
            });

            let subTitle = description || title;

            if ((quantity > 1 || category === "variable_fee") && category !== "salary") {
                subTitle = `${quantity} × ${subTitle}`;
            } else if (category === "bonus") {
                subTitle = i18n.t("screens.payroll_breakdown_editor.product_keys.with_charges");
            } else if (productKey === "salary_package") {
                switch (contractEarningType) {
                    case EarningType.Daily:
                        subTitle = i18n.t("screens.payroll_breakdown_editor.product_keys.nb_days", {
                            quantity,
                        });
                        break;
                    case EarningType.Hourly:
                        subTitle = i18n.t(
                            "screens.payroll_breakdown_editor.product_keys.nb_hours",
                            {
                                quantity,
                            },
                        );
                        break;
                    default:
                        break;
                }
            }

            return {
                label: subTitle,
                value: formatCurrency({
                    amountInCents: unitPrice * quantity,
                    displayCurrencySymbol: false,
                }),
            };
        },
        [contractEarningType, i18n],
    );

    const getLabelValue = useCallback(
        (item: Omit<SummaryRowWithSubValues, "category">) => {
            const { productKey, quantity, description, unitPrice, subValues = [] } = item;

            const title = i18n.t(`screens.payroll_breakdown_editor.product_keys.${productKey}`, {
                quantity: item.quantity,
                unitPrice: formatCurrency({
                    amountInCents: item.unitPrice,
                }),
            });

            return {
                label: `${title}${description ? ` : ${description}` : ""}`,
                value: formatCurrency({
                    amountInCents:
                        subValues.length === 0
                            ? unitPrice * quantity
                            : subValues.reduce((r, v) => r + v.unitPrice * v.quantity, 0),
                    displayCurrencySymbol: false,
                }),
                subValues: subValues.map(getSubLabelValue),
            };
        },
        [getSubLabelValue, i18n],
    );
    const groupItems = useCallback((items: SummaryRowWithSubValues[], groupCategory: string) => {
        const subValues = items.filter((i) => i.category === groupCategory);

        return {
            productKey: groupCategory,
            unitPrice: subValues.reduce((r, v) => r + v.unitPrice * v.quantity, 0),
            quantity: 1,
            subValues,
        } as Omit<SummaryRowWithSubValues, "category">;
    }, []);

    const groupedInvoicesItems = useMemo(
        () =>
            summaryRows.reduce(
                (result, value) => {
                    const rowIndex = result.findIndex((row) => row.productKey === value.category);

                    if (rowIndex === -1) {
                        if (
                            // Categories that we want to group
                            [
                                "salary",
                                "deduction",
                                "effective_fee",
                                "dynamic_fee",
                                "variable_fee",
                                "bonus",
                                "vacation_compensation",
                            ].includes(value.category)
                        ) {
                            const children = groupItems(summaryRows, value.category);
                            return [...result, children];
                        } else {
                            return [...result, value];
                        }
                    } else {
                        return result;
                    }
                },
                [] as Omit<SummaryRowWithSubValues, "category">[],
            ),
        [groupItems, summaryRows],
    );

    return (
        <VStack h="2/3" gap={1}>
            <TitleMediumText>{i18n.t("screens.payroll_breakdown_editor.preview")}</TitleMediumText>

            {needCompute ? (
                <Callout
                    markdown={i18n.t("screens.payroll_breakdown_editor.preview_need_compute")}
                    mode="warning"
                />
            ) : rows.length === 0 ? (
                <Callout
                    markdown={i18n.t("screens.payroll_breakdown_editor.no_data")}
                    mode="info"
                />
            ) : !loading && !error ? (
                <VStack gap={1}>
                    {groupedInvoicesItems.map((row, index) => {
                        return <LabelValueText key={index} {...getLabelValue(row)} />;
                    })}
                    <LabelValueText
                        label={i18n.t("screens.payroll_breakdown_editor.invoice")}
                        value={formatCurrency({
                            amountInCents: groupedInvoicesItems.reduce(
                                (result, row) =>
                                    result +
                                    (row.subValues
                                        ? row.subValues.reduce(
                                              (r, v) => r + v.unitPrice * v.quantity,
                                              0,
                                          )
                                        : row.unitPrice * row.quantity),
                                0,
                            ),
                            displayCurrencySymbol: false,
                        })}
                        textStyle={{ fontWeight: "bold" }}
                    />
                </VStack>
            ) : error ? (
                <ScreenCardHeadband
                    text={i18n.t(
                        error.message || "screens.payroll_breakdown_editor.error_getting_summary",
                    )}
                    mode="error"
                />
            ) : (
                <Loading />
            )}
        </VStack>
    );
};

export default PayrollBreakdownSummary;
